import { useEffect, useState } from "react";
import { authRefresh, logout, getToken, SESSION_EXPIRED_EVENT } from "./lib/pb";
import LoginPage from "./pages/LoginPage";
import ArticlesPage from "./pages/ArticlesPage";
import ArticleEditPage from "./pages/ArticleEditPage";
import TagsPage from "./pages/TagsPage";
import AuditLogPage from "./pages/AuditLogPage";
import DashboardPage from "./pages/DashboardPage";
import DraftsPage from "./pages/DraftsPage";
import ScheduledPage from "./pages/ScheduledPage";
import MediaLibraryPage from "./pages/MediaLibraryPage";
import SeriesPage from "./pages/SeriesPage";
import SettingsPage from "./pages/SettingsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import GlobalSearch from "./components/GlobalSearch";
import OnboardingFlow, { isOnboardingNeeded } from "./components/OnboardingFlow";
import { useT } from "./lib/i18n";
import type { Editor } from "./lib/types";

// Hash-router — 10 routes now; still light enough to skip react-router.
type Route =
  | { name: "dashboard" }
  | { name: "analytics" }
  | { name: "articles" }
  | { name: "article-edit"; id: string | "new" }
  | { name: "drafts" }
  | { name: "scheduled" }
  | { name: "series" }
  | { name: "tags" }
  | { name: "media" }
  | { name: "audit" }
  | { name: "settings" };

function parseHash(): Route {
  const h = window.location.hash.replace(/^#/, "");
  if (h.startsWith("/article/")) {
    return { name: "article-edit", id: h.slice("/article/".length) };
  }
  if (h === "/dashboard" || h === "" || h === "/") return { name: "dashboard" };
  if (h === "/analytics") return { name: "analytics" };
  if (h === "/articles") return { name: "articles" };
  if (h === "/drafts") return { name: "drafts" };
  if (h === "/scheduled") return { name: "scheduled" };
  if (h === "/series") return { name: "series" };
  if (h === "/tags") return { name: "tags" };
  if (h === "/media") return { name: "media" };
  if (h === "/audit") return { name: "audit" };
  if (h === "/settings") return { name: "settings" };
  return { name: "dashboard" };
}

export function navigate(path: string) {
  window.location.hash = path;
}

export default function App() {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  // Сессия протухла во время работы (а не «просто не залогинен») — показываем
  // на экране входа пояснение, чтобы это не выглядело случайным разлогином.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [route, setRoute] = useState<Route>(parseHash());
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem("reverium-sidebar-collapsed") === "1"; }
    catch { return false; }
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Запускаем онбординг только когда юзер успешно вошёл (editor != null) —
  // login-форма должна оставаться чистой. Проверяем флаг один раз per
  // editor change (повторно не дёргается на ререндерах).
  useEffect(() => {
    if (editor && isOnboardingNeeded()) {
      setShowOnboarding(true);
    }
  }, [editor]);

  // Слушаем глобальное событие "replay onboarding" — диспатчится из
  // Settings → кнопка «Пройти онбординг». Не таскаем callback через
  // 5 уровней пропсов.
  useEffect(() => {
    function onReplay() { setShowOnboarding(true); }
    window.addEventListener("reverium-editorial-replay-onboarding", onReplay);
    return () => window.removeEventListener("reverium-editorial-replay-onboarding", onReplay);
  }, []);

  useEffect(() => {
    const onHash = () => setRoute(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Cmd/Ctrl+K — open global search anywhere.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!getToken()) {
      setAuthLoading(false);
      return;
    }
    authRefresh()
      .then((r) => {
        if (r && r.record.active) {
          setEditor(r.record as Editor);
        } else if (r && !r.record.active) {
          logout();
          alert("Your editor account is deactivated. Contact admin.");
        }
      })
      .finally(() => setAuthLoading(false));
  }, []);

  // ── Протухшая сессия → экран входа ──────────────────────────────────────
  // pb.api() при 401 чистит токен и шлёт это событие. Без обработчика админка
  // просто молча переставала грузить данные (баг Ивана 2026-07-24).
  useEffect(() => {
    const onExpired = () => {
      setEditor(null);
      setSessionExpired(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  // ── Проактивное продление сессии ────────────────────────────────────────
  // Чтобы до 401 вообще не доходило: пока редактор залогинен, освежаем токен
  // раз в 30 минут и при возврате фокуса на вкладку (админку часто держат
  // открытой сутками). authRefresh сам чистит токен, если сервер отказал, —
  // тогда следующий же запрос отдаст 401 и сработает обработчик выше.
  useEffect(() => {
    if (!editor) return;
    const refresh = () => { void authRefresh(); };
    const id = setInterval(refresh, 30 * 60 * 1000);
    window.addEventListener("focus", refresh);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", refresh);
    };
  }, [editor]);

  if (authLoading) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--text-3)",
        }}
      >
        Loading…
      </div>
    );
  }

  if (!editor) {
    return (
      <LoginPage
        sessionExpired={sessionExpired}
        onLogin={(e) => { setSessionExpired(false); setEditor(e); }}
      />
    );
  }

  // ArticleEditPage теперь встраивается в app-shell (sidebar остаётся виден).
  // is-editor выключает overflow:auto на main, ArticleEditPage сам управляет
  // прокруткой своих колонок.
  const isEditor = route.name === "article-edit";

  return (
    <div className="app-shell">
      <Sidebar
        route={route}
        editor={editor}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => {
          const v = !sidebarCollapsed;
          setSidebarCollapsed(v);
          try { localStorage.setItem("reverium-sidebar-collapsed", v ? "1" : "0"); } catch { /* ignore */ }
        }}
        onLogout={() => {
          logout();
          setEditor(null);
        }}
      />
      <main className={`app-main${isEditor ? " is-editor" : ""}`}>
        {route.name === "dashboard" && <DashboardPage />}
        {route.name === "analytics" && <AnalyticsPage />}
        {route.name === "articles" && <ArticlesPage />}
        {route.name === "article-edit" && <ArticleEditPage articleId={route.id} editor={editor} />}
        {route.name === "drafts" && <DraftsPage />}
        {route.name === "scheduled" && <ScheduledPage />}
        {route.name === "series" && <SeriesPage />}
        {route.name === "tags" && <TagsPage />}
        {route.name === "media" && <MediaLibraryPage />}
        {route.name === "audit" && <AuditLogPage />}
        {route.name === "settings" && <SettingsPage editor={editor} onUpdated={(e) => setEditor(e)} />}
      </main>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      {showOnboarding && <OnboardingFlow onClose={() => setShowOnboarding(false)} />}
    </div>
  );
}

interface NavItem {
  key: Route["name"];
  labelKey: keyof ReturnType<typeof useT>;
  href: string;
  icon: string;
}

const NAV_ITEMS: NavItem[] = [
  { key: "dashboard",  labelKey: "navDashboard",  href: "#/dashboard",  icon: "◇" },
  { key: "analytics",  labelKey: "navAnalytics",  href: "#/analytics",  icon: "⊞" },
  { key: "articles",   labelKey: "navArticles",   href: "#/articles",   icon: "▦" },
  { key: "drafts",    labelKey: "navDrafts",    href: "#/drafts",    icon: "✎" },
  { key: "scheduled", labelKey: "navScheduled", href: "#/scheduled", icon: "◷" },
  { key: "series",    labelKey: "navSeries",    href: "#/series",    icon: "❏" },
  { key: "tags",      labelKey: "navTags",      href: "#/tags",      icon: "#" },
  { key: "media",     labelKey: "navMedia",     href: "#/media",     icon: "▢" },
  { key: "audit",     labelKey: "navAudit",     href: "#/audit",     icon: "≡" },
  { key: "settings",  labelKey: "navSettings",  href: "#/settings",  icon: "⚙" },
];

function Sidebar({
  route,
  editor,
  collapsed,
  onToggleCollapsed,
  onLogout,
}: {
  route: Route;
  editor: Editor;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onLogout: () => void;
}) {
  const t = useT();
  // The current article-edit route highlights "articles" in the sidebar — это
  // правильно семантически (юзер всё ещё в разделе Articles).
  const activeKey: Route["name"] =
    route.name === "article-edit" ? "articles" : route.name;
  const initials = editorInitials(editor);
  return (
    <aside className={`sidebar ${collapsed ? "collapsed" : ""}`}>
      {!collapsed ? (
        <div className="sidebar-brand">
          <span className="sidebar-brand-logo">
            <img src="/r-logo.png" alt="" />
          </span>
          <span className="sidebar-brand-text">
            <span className="sidebar-brand-name">Reverium</span>
            <span className="sidebar-brand-mark">Editorial</span>
          </span>
        </div>
      ) : (
        <div className="sidebar-brand-collapsed" title="Reverium Editorial">
          <span className="sidebar-brand-logo">
            <img src="/r-logo.png" alt="Reverium" />
          </span>
        </div>
      )}

      <nav className="sidebar-nav">
        {NAV_ITEMS.map((it) => {
          const isActive = activeKey === it.key;
          const label = t[it.labelKey] as string;
          return (
            <a
              key={it.key}
              href={it.href}
              className={`sidebar-item ${isActive ? "active" : ""}`}
              title={collapsed ? label : undefined}
              style={collapsed ? { justifyContent: "center", padding: "8px 0" } : undefined}
            >
              <span className="sidebar-icon">{it.icon}</span>
              {!collapsed && <span>{label}</span>}
            </a>
          );
        })}
      </nav>

      <div style={{ flex: 1 }} />

      <div className="sidebar-footer">
        {!collapsed && (
          <>
            <div className="sidebar-user">
              <span className="sidebar-user-avatar">{initials}</span>
              <span className="sidebar-user-meta">
                <span className="sidebar-user-name">{editor.name || editor.email}</span>
                <span className="sidebar-user-email">{editor.email}</span>
              </span>
            </div>
            <button onClick={onLogout} className="btn btn-ghost btn-sm" style={{ justifyContent: "flex-start" }}>
              {t.signOut}
            </button>
          </>
        )}
        <button
          onClick={onToggleCollapsed}
          className="sidebar-collapse-btn"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={collapsed ? { justifyContent: "center" } : undefined}
        >
          {collapsed ? "›" : "‹  Collapse"}
        </button>
      </div>
    </aside>
  );
}

function editorInitials(e: Editor): string {
  const name = (e.name || e.email || "").trim();
  if (!name) return "·";
  const parts = name.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
